<script setup lang="ts">
import { onMounted, ref } from "vue";

import { apiClient } from "@/client";
import ToastNotification from "@/components/ToastNotification.vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import type { ShortLink, ShortLinkAnalytics } from "@/type/api";

const links = ref<ShortLink[]>([]);
const loading = ref(false);
const error = ref("");

// Create form
const linkName = ref("");
const targetUrl = ref("");
const creating = ref(false);

// Inline edit
const editingId = ref<number | null>(null);
const editTarget = ref("");
const editDisabled = ref(false);
const saving = ref(false);

// Analytics expand
const expandedId = ref<number | null>(null);
const analyticsCache = ref<Record<number, ShortLinkAnalytics>>({});
const analyticsLoadingId = ref<number | null>(null);

// Toast
const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);
function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
	setTimeout(() => {
		toastShow.value = false;
	}, 4000);
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		links.value = await apiClient.JSONGet<ShortLink[]>("/api/short-link");
	} catch (e: any) {
		error.value = e?.message || "Failed to load links.";
	} finally {
		loading.value = false;
	}
}

async function createLink() {
	const name = linkName.value.trim();
	const target = targetUrl.value.trim();
	if (name === "" && target === "") {
		showNotification("Missing input", "Provide a name or a target URL.");
		return;
	}
	creating.value = true;
	try {
		const created = await apiClient.JSONPost<ShortLink>("/api/short-link", {
			name,
			target,
		});
		showNotification("Link created", created.link);
		linkName.value = "";
		targetUrl.value = "";
		await load();
	} catch (e: any) {
		showNotification(
			"Failed to create link",
			e?.message || "Something went wrong.",
		);
	} finally {
		creating.value = false;
	}
}

function startEdit(link: ShortLink) {
	editingId.value = link.id;
	editTarget.value = link.target;
	editDisabled.value = link.disabled;
}

function cancelEdit() {
	editingId.value = null;
	editTarget.value = "";
	editDisabled.value = false;
}

async function saveEdit(link: ShortLink) {
	saving.value = true;
	try {
		await apiClient.JSONPut<ShortLink>(`/api/short-link/${link.id}`, {
			target: editTarget.value,
			disabled: editDisabled.value,
		});
		cancelEdit();
		showNotification("Link updated", `${link.code} updated.`);
		await load();
	} catch (e: any) {
		showNotification(
			"Failed to update link",
			e?.message || "Something went wrong.",
		);
	} finally {
		saving.value = false;
	}
}

async function copyLink(link: string) {
	try {
		await navigator.clipboard.writeText(link);
		showNotification("Link copied", link);
	} catch {
		showNotification(
			"Copy failed",
			"Could not copy the link to your clipboard.",
		);
	}
}

async function downloadClicksCsv(link: ShortLink) {
	try {
		const blob = await apiClient.JSONGet<Blob>(
			`/api/short-link/${link.id}/clicks.csv`,
			{ responseType: "blob" },
		);
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `short-link-${link.code}-clicks.csv`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
		showNotification("Export ready", a.download);
	} catch (e: any) {
		showNotification(
			"Failed to export clicks",
			e?.message || "Something went wrong.",
		);
	}
}

function formatDay(day: string): string {
	return day.slice(0, 10);
}

async function toggleExpand(link: ShortLink) {
	if (expandedId.value === link.id) {
		expandedId.value = null;
		return;
	}
	expandedId.value = link.id;
	if (analyticsCache.value[link.id]) {
		return;
	}
	analyticsLoadingId.value = link.id;
	try {
		analyticsCache.value[link.id] = await apiClient.JSONGet<ShortLinkAnalytics>(
			`/api/short-link/${link.id}/clicks`,
		);
	} catch (e: any) {
		showNotification(
			"Failed to load analytics",
			e?.message || "Something went wrong.",
		);
	} finally {
		analyticsLoadingId.value = null;
	}
}

function csvEscape(value: unknown): string {
	const s = value === null || value === undefined ? "" : String(value);
	if (/[",\n\r]/.test(s)) {
		return `"${s.replace(/"/g, '""')}"`;
	}
	return s;
}

function exportCsv() {
	const headers = [
		"code",
		"link",
		"target",
		"campaign",
		"disabled",
		"clicks",
		"last_clicked_at",
		"created",
	];
	const rows = links.value.map((l) => [
		l.code,
		l.link,
		l.target,
		l.campaign ?? "",
		l.disabled,
		l.clicks,
		l.last_clicked_at ?? "",
		l.created,
	]);
	const csv = [headers, ...rows]
		.map((r) => r.map(csvEscape).join(","))
		.join("\n");
	const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = "short-links.csv";
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
	showNotification("Export ready", "short-links.csv downloaded.");
}

onMounted(load);
</script>

<template>
	<div class="container py-5">
		<div class="row mb-4">
			<div class="col">
				<h1 class="display-6 mb-3">Link Manager</h1>
				<p class="text-muted lead">
					Create short links for mosquito service URLs and track where residents
					click through from.
				</p>
			</div>
		</div>

		<div class="row g-4">
			<!-- Create card -->
			<div class="col-12">
				<div class="card border-0 shadow-sm">
					<div class="card-body p-4">
						<h2 class="h5 mb-3">New Link</h2>
						<div class="row g-3">
							<div class="col-12 col-md-6">
								<label class="form-label" for="link-name"
									>Name (optional)</label
								>
								<input
									id="link-name"
									v-model="linkName"
									class="form-control"
									type="text"
									placeholder="e.g. summer 2026"
								/>
								<div class="form-text">
									Becomes the link's short code (summer 2026 → /l/summer-2026)
									and the campaign label. Leave blank for a random code.
								</div>
							</div>
							<div class="col-12 col-md-6">
								<label class="form-label" for="target-url"
									>Or a custom target URL</label
								>
								<input
									id="target-url"
									v-model="targetUrl"
									class="form-control"
									type="text"
									placeholder="https://report.mosquitoes.online/..."
								/>
								<div class="form-text">
									Optional — without one, this becomes a campaign link that
									starts a Mosquito Check.
								</div>
							</div>
						</div>
						<button
							class="btn btn-primary mt-3"
							:disabled="creating"
							@click="createLink"
						>
							<i class="bi bi-link-45deg me-1"></i>
							{{ creating ? "Creating…" : "Create link" }}
						</button>
					</div>
				</div>
			</div>

			<!-- List card -->
			<div class="col-12">
				<div class="card border-0 shadow-sm">
					<div class="card-body p-4">
						<div class="d-flex align-items-center justify-content-between mb-3">
							<h2 class="h5 mb-0">Links</h2>
							<button
								class="btn btn-sm btn-outline-secondary"
								:disabled="links.length === 0"
								title="Download the link list as CSV"
								@click="exportCsv"
							>
								<i class="bi bi-download me-1"></i>
								Export CSV
							</button>
						</div>
						<div v-if="loading" class="text-muted py-4 text-center">
							Loading…
						</div>
						<div v-else-if="error" class="alert alert-danger mb-0">
							{{ error }}
						</div>
						<div
							v-else-if="links.length === 0"
							class="text-muted py-4 text-center"
						>
							No links yet — create one above.
						</div>
						<div v-else class="table-responsive">
							<table class="table align-middle mb-0">
								<thead>
									<tr>
										<th>Link</th>
										<th>Campaign</th>
										<th>Clicks</th>
										<th>Last clicked</th>
										<th>Created</th>
										<th>Status</th>
										<th class="text-end">Actions</th>
									</tr>
								</thead>
								<tbody>
									<template v-for="link in links" :key="link.id">
										<tr :class="{ 'table-secondary': link.disabled }">
											<td>
												<button
													class="btn btn-sm btn-link p-0 me-2 align-baseline"
													:title="
														expandedId === link.id
															? 'Hide analytics'
															: 'Show analytics'
													"
													@click="toggleExpand(link)"
												>
													<i
														:class="
															expandedId === link.id
																? 'bi bi-chevron-down'
																: 'bi bi-chevron-right'
														"
													></i>
												</button>
												<code class="me-2">{{ link.code }}</code>
												<span v-if="editingId === link.id" class="d-block mt-1">
													<input
														v-model="editTarget"
														class="form-control form-control-sm"
														type="text"
														placeholder="https://report.mosquitoes.online/..."
													/>
												</span>
												<span
													v-else
													class="text-muted small d-block text-truncate"
													style="max-width: 18rem"
												>
													{{ link.target }}
												</span>
											</td>
											<td>
												<span
													v-if="link.campaign"
													class="badge bg-info text-dark"
												>
													{{ link.campaign }}
												</span>
												<span v-else class="text-muted">—</span>
											</td>
											<td>{{ link.clicks }}</td>
											<td>
												<TimeRelativeWithTooltip
													v-if="link.last_clicked_at"
													:time="link.last_clicked_at"
												/>
												<span v-else class="text-muted">—</span>
											</td>
											<td>
												<TimeRelativeWithTooltip :time="link.created" />
											</td>
											<td>
												<span v-if="editingId === link.id" class="form-check">
													<input
														id="edit-disabled"
														v-model="editDisabled"
														class="form-check-input"
														type="checkbox"
													/>
													<label class="form-check-label" for="edit-disabled">
														Disabled
													</label>
												</span>
												<span
													v-else-if="link.disabled"
													class="badge bg-secondary"
												>
													Disabled
												</span>
												<span v-else class="badge bg-success">Active</span>
											</td>
											<td class="text-end">
												<template v-if="editingId === link.id">
													<button
														class="btn btn-sm btn-success me-1"
														:disabled="saving"
														@click="saveEdit(link)"
													>
														<i class="bi bi-check-lg me-1"></i>
														Save
													</button>
													<button
														class="btn btn-sm btn-outline-secondary"
														:disabled="saving"
														@click="cancelEdit"
													>
														Cancel
													</button>
												</template>
												<template v-else>
													<div class="d-inline-flex gap-2">
														<button
															class="btn btn-sm btn-outline-secondary btn-icon"
															title="Edit link"
															@click="startEdit(link)"
														>
															<i class="bi bi-pencil"></i>
														</button>
														<button
															class="btn btn-sm btn-outline-secondary btn-icon"
															title="Download click history as CSV"
															@click="downloadClicksCsv(link)"
														>
															<i class="bi bi-download"></i>
														</button>
														<button
															class="btn btn-sm btn-outline-secondary btn-icon"
															title="Copy link"
															@click="copyLink(link.link)"
														>
															<i class="bi bi-clipboard"></i>
														</button>
													</div>
												</template>
											</td>
										</tr>
										<tr v-if="expandedId === link.id" class="analytics-row">
											<td colspan="7" class="bg-light">
												<div
													v-if="analyticsLoadingId === link.id"
													class="text-muted py-3 text-center"
												>
													Loading analytics…
												</div>
												<div
													v-else-if="analyticsCache[link.id]"
													class="row g-4 py-2"
												>
													<div class="col-12 col-md-6">
														<h3 class="h6">Clicks per day (last 30 days)</h3>
														<div class="table-responsive">
															<table class="table table-sm table-striped mb-0">
																<thead>
																	<tr>
																		<th>Date</th>
																		<th class="text-end">Clicks</th>
																	</tr>
																</thead>
																<tbody>
																	<tr
																		v-for="d in analyticsCache[link.id].daily"
																		:key="d.day"
																	>
																		<td>{{ formatDay(d.day) }}</td>
																		<td class="text-end">{{ d.clicks }}</td>
																	</tr>
																</tbody>
															</table>
														</div>
													</div>
													<div class="col-12 col-md-6">
														<h3 class="h6">Recent clicks</h3>
														<div
															v-if="analyticsCache[link.id].recent.length === 0"
															class="text-muted"
														>
															No clicks yet.
														</div>
														<ul v-else class="list-unstyled small mb-0">
															<li
																v-for="(c, i) in analyticsCache[link.id].recent"
																:key="i"
																class="d-flex justify-content-between gap-3 py-1 border-bottom"
															>
																<TimeRelativeWithTooltip :time="c.clicked_at" />
																<span
																	v-if="c.referrer"
																	class="text-truncate text-muted"
																	style="max-width: 18rem"
																	:title="c.referrer"
																>
																	{{ c.referrer }}
																</span>
																<span v-else class="text-muted">—</span>
															</li>
														</ul>
													</div>
												</div>
											</td>
										</tr>
									</template>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
		/>
	</div>
</template>

<style scoped>
/* Icon-only action buttons: fixed square, glyph optically centered.
   Bootstrap's font icons ride high in their line box (ascent-heavy), so
   line-height:1 collapses the box and flex centers the actual glyph. */
.btn-icon {
	width: 34px;
	height: 34px;
	padding: 0;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	line-height: 1;
}
.btn-icon i,
.btn-icon i::before {
	line-height: 1;
}
</style>
